/**
 * The event registry — the single source of truth for what this product may emit.
 *
 * CONFIDENTIAL — ASI Internal.
 * Copyright 2026, Partnership Net; Agency for Sponsorship Intelligence, Lda;
 * Advanced Sponsorship Insights, GmbH. Confidential and proprietary.
 *
 * REGISTRY FIRST. Nothing emits an event that is not declared here: the emit client is
 * typed from this object, so an unregistered code is a compile error rather than a
 * runtime surprise, and a property outside an event's allow-list is stripped before it
 * can travel. Free-text event names are how an analytics estate dies inside eighteen
 * months — three variants of one event, and numbers that stop agreeing.
 *
 * TWO TIERS, AND THE WALL BETWEEN THEM IS ONE-DIRECTIONAL.
 *   Tier A — METER. Billing truth. SERVER-SIDE ONLY: a browser can be tampered with and a
 *            disputed invoice has to be defensible, so the browser never emits one. The
 *            entries below exist so the meter is designed before the endpoint is written,
 *            and each names the server emission point that will carry it.
 *   Tier B — SIGNAL. Product analytics and purchase intent. May be emitted from the
 *            client, cookieless and memory-only until consent is captured, and gated per
 *            tenant on the service-usage clause. A Tier B outage never touches an invoice.
 *
 * AN EVENT NAME IS A MOAT CONTROL, NOT A STYLE GUIDE. Event names and properties travel
 * to analytics platforms, logs and support tooling, so they name the SURFACE and the VERB
 * — never a driver, a coefficient, a threshold, a weight or a formula. Counts travel; the
 * values counted do not. `tests/telemetry/registry.test.ts` fails the build on a breach.
 *
 * WHAT IS NOT BUILT HERE, and is not pretended to be: there is no transport. The client
 * writes Tier B events to a memory sink and nothing leaves the browser. When the governed
 * access layer lands, the sink gets a transport and this file does not change.
 */

export type Tier = 'A' | 'B';

export type LegalBasis =
  /** Tier A only: we meter because the contract requires us to bill accurately. */
  | 'contractual-necessity'
  /** Tier B only: legitimate interest, plus the signed service-usage controller clause. */
  | 'legitimate-interest';

export interface EventDef {
  /** Dot-separated, lower case: product.surface.verb. */
  readonly code: string;
  readonly tier: Tier;
  readonly basis: LegalBasis;
  /**
   * Whether this event is the thing an invoice is built from. `false` carries a reason —
   * silence is not an option, because an unmetered billable surface is lost revenue
   * nobody notices.
   */
  readonly billable: boolean;
  readonly notBillableBecause?: string;
  /** What one event counts: an engagement, a dataset, a delivery. */
  readonly unit: string;
  /** The user-facing surface it belongs to. */
  readonly surface: string;
  /**
   * Where it is emitted from. Tier A must name a server endpoint — and while that
   * endpoint does not exist, it says so rather than implying a meter that is running.
   */
  readonly emission: string;
  /** The ONLY properties this event may carry. Anything else is stripped. */
  readonly properties: readonly string[];
  /** Role, never a person. */
  readonly owner: string;
}

const A = 'contractual-necessity' as const;
const B = 'legitimate-interest' as const;

/**
 * The declared events.
 *
 * Read the Tier A block as a design, not as a running meter: every one of them is emitted
 * by a server that has not been built (DEC-13 blocks the API on the arena access layer),
 * and `tests/telemetry/registry.test.ts` asserts that the browser client refuses them.
 */
export const EVENTS = {
  /* ---------------------------------------------------- Tier A — meters -- */

  'mmm.engagement.committed': {
    code: 'mmm.engagement.committed',
    tier: 'A',
    basis: A,
    billable: true,
    unit: 'engagement',
    surface: 'MMM Integration · Accept & Kickoff',
    emission: 'server — not built (the commit endpoint lands with the MMM API)',
    // Counts only. Never a partner, a brand, a contract reference or a metric name.
    properties: [
      'contracts_count',
      'markets_count',
      'metrics_count',
      'periods_count',
      'services_count',
      'custom_taxonomy',
    ],
    owner: 'Product Owner',
  },
  'mmm.dataset.accepted': {
    code: 'mmm.dataset.accepted',
    tier: 'A',
    basis: A,
    billable: true,
    unit: 'dataset',
    surface: 'MMM Integration · Confirm Results',
    emission: 'server — not built',
    properties: ['rows_count', 'variables_count', 'markets_count'],
    owner: 'Product Owner',
  },
  'mmm.delivery.validated': {
    code: 'mmm.delivery.validated',
    tier: 'A',
    basis: A,
    billable: true,
    unit: 'delivery',
    surface: 'MMM Integration · MMM Integration exchange',
    emission: 'server — not built',
    properties: ['rows_count', 'blockers_count', 'weeks_covered'],
    owner: 'Product Owner',
  },

  /*
   * Partnership Business Case — two commit moments, one billable (DEC-59).
   *
   * `calculate` runs the simulation; `freeze` fixes the case as final. The PO ruling of
   * 18 Sep meters both and bills only the second: a case is iterated many times before
   * anyone is willing to stand behind it, and charging per run would suppress exactly the
   * iteration the product exists to encourage — while destroying the intent signal in the
   * same move. The run is still metered, because a simulation has real per-tenant serving
   * cost and usage pricing without cost visibility loses money on its best customers.
   * Bill on value, meter on cost, keep both in one ledger.
   *
   * NOTE ON PROPERTIES. Not `drivers_count` — `driver` is on the moat denylist, and an
   * analytics platform receiving a driver-named event fifty thousand times has been handed
   * the value-driver taxonomy in plain text with a timestamp distribution. `inputs_count`
   * says as much as anyone outside needs to know.
   */
  'proi.case.calculated': {
    code: 'proi.case.calculated',
    tier: 'A',
    basis: A,
    billable: false,
    notBillableBecause:
      'Exploration is free. The run is metered for per-tenant serving cost, not billed (DEC-59).',
    unit: 'run',
    surface: 'Partnership Business Case · Calculate',
    emission: 'server — proi-agent-backend POST /cases/:id/calculate (not wired)',
    properties: ['inputs_count', 'rights_count', 'activations_count', 'kind'],
    owner: 'Product Owner',
  },
  'proi.case.frozen': {
    code: 'proi.case.frozen',
    tier: 'A',
    basis: A,
    billable: true,
    unit: 'case',
    surface: 'Partnership Business Case · Freeze',
    emission: 'server — proi-agent-backend POST /cases/:id/freeze (not wired)',
    properties: ['inputs_count', 'rights_count', 'runs_count'],
    owner: 'Product Owner',
  },

  /* ---------------------------------------------------- Tier B — signals -- */

  'agent.step.viewed': {
    code: 'agent.step.viewed',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause: 'Exploration. Walking a flow is free and must stay free.',
    unit: 'view',
    surface: 'Agent shell',
    emission: 'client — app shell chokepoint',
    // `step` is a permanent surface key. It is not a driver and carries no value.
    properties: ['agent', 'step'],
    owner: 'Product Owner',
  },
  'agent.export.requested': {
    code: 'agent.export.requested',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause:
      'Value realisation, not a billable commit — the commit it belongs to is metered separately.',
    unit: 'export',
    surface: 'Agent exports',
    properties: ['agent', 'surface', 'format'],
    emission: 'client — export controls',
    owner: 'Product Owner',
  },
  'mmm.variable_creation.calculated': {
    code: 'mmm.variable_creation.calculated',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause:
      'Exploration: a plan can be recalculated all day. The dataset it produces is the commit.',
    unit: 'run',
    surface: 'MMM Integration · Variable Creation',
    emission: 'client — step control',
    // A count and a yes/no. Never the threshold, the percentile or what was demoted.
    properties: ['variables_count', 'capacity_declared'],
    owner: 'Product Owner',
  },
  'inventory.analysis.completed': {
    code: 'inventory.analysis.completed',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause:
      'The inventory read is part of the build that is already metered at commit.',
    unit: 'run',
    surface: 'Data inventory',
    emission: 'client — module lifecycle',
    properties: ['partners_count', 'sources_count', 'unknown_count'],
    owner: 'Product Owner',
  },
  'catalogue.beta.registered': {
    code: 'catalogue.beta.registered',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause:
      'Demand for something that does not exist yet. Registering is free and stays free — charging for it would destroy the signal it exists to collect.',
    unit: 'registration',
    surface: 'Lockerroom · catalogue',
    emission: 'client — card control',
    // `surface` is the module id and `kind` is agent-or-report: two permanent
    // surface keys. No identity, no free text, nothing about who registered —
    // the question is which capability is wanted next, not by whom.
    properties: ['surface', 'kind'],
    owner: 'Product Owner',
  },
  'inventory.register.edited': {
    code: 'inventory.register.edited',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause: 'Maintaining an inventory is the work the engagement already pays for.',
    unit: 'edit',
    surface: 'Data inventory · Edit & update',
    emission: 'client — module control',
    properties: ['kind', 'cells_count'],
    owner: 'Product Owner',
  },
  /* --------------------------------- Tier B - the intent-signal additions -- */

  'agent.flow.started': {
    code: 'agent.flow.started',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause: 'Starting something is free. The commit at the end of it is what bills.',
    unit: 'flow',
    surface: 'Agent shell',
    emission: 'client - app shell chokepoint',
    properties: ['agent'],
    owner: 'Product Owner',
  },
  'agent.flow.completed': {
    code: 'agent.flow.completed',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause:
      'The completion signal is behavioural. Where a completed flow is also a billable commit, the meter is emitted server-side and separately.',
    unit: 'flow',
    surface: 'Agent shell',
    emission: 'client - flow terminal step',
    // Paired with agent.flow.started, this is the ONLY way to tell an abandonment from
    // a revisit. Step views alone cannot: a user who walks back to re-read step 7 and a
    // user who gave up at step 7 produce identical step-view rows.
    properties: ['agent', 'steps_count'],
    owner: 'Product Owner',
  },
  'paywall.encountered': {
    code: 'paywall.encountered',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause: 'Being stopped by a limit is not a use of anything.',
    unit: 'encounter',
    surface: 'All modules',
    emission: 'client - limit guard',
    // The surface and the KIND of limit reached - never a percentage, a plan or an
    // entitlement. The client does not know true consumption; the meter does, and it is
    // server-side. Shipping a client-asserted percentage here would put a number in the
    // Cockpit that nothing can reconcile against the invoice.
    properties: ['surface', 'kind'],
    owner: 'Product Owner',
  },
  'seat.first_activity': {
    code: 'seat.first_activity',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause: 'A seat becoming active is an expansion signal, not a billable action.',
    unit: 'activation',
    surface: 'Identity',
    emission: 'client - first authenticated action in a session',
    properties: ['surface'],
    owner: 'Product Owner',
  },

  /* -------------------------------------------- Tier B - Human Layer settings -- */

  'human_layer.profile.updated': {
    code: 'human_layer.profile.updated',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause: 'Configuring a personal preference is not a use of anything to bill.',
    unit: 'update',
    surface: 'Settings · Human Layer · My Profile',
    emission: 'client — profile form save',
    // Counts only — never the name, role, team, territory or language that was saved.
    properties: ['territory_count', 'notify_on_count'],
    owner: 'Product Owner',
  },
  'human_layer.voice.updated': {
    code: 'human_layer.voice.updated',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause: 'Configuring a personal preference is not a use of anything to bill.',
    unit: 'update',
    surface: 'Settings · Human Layer · My Voice',
    emission: 'client — voice form save',
    // A count and a category only — never the phrases, the sample text or the
    // tone scores that were actually saved. `kind` carries the source method
    // (this slice: always 'pasted_text') — a category, not the sample itself.
    properties: ['signature_phrases_count', 'kind'],
    owner: 'Product Owner',
  },
  'human_layer.voice.section_captured': {
    code: 'human_layer.voice.section_captured',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause: 'Saving one interview section is not a use of anything to bill.',
    unit: 'save',
    surface: 'Settings · Human Layer · My Voice · Capture',
    emission: 'client — per-section save',
    // `step` is a permanent surface key (same shape as the agent shell's own
    // `step` property, above) — which of the nine closed section ids was
    // saved, never the transcript text that was actually typed, pasted or
    // uploaded into it.
    properties: ['step'],
    owner: 'Product Owner',
  },
  'human_layer.voice.analyzed': {
    code: 'human_layer.voice.analyzed',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause: 'Running the placeholder tone estimate is not a use of anything to bill.',
    unit: 'run',
    surface: 'Settings · Human Layer · My Voice · Analyze',
    emission: 'client — Analyze button',
    // How many samples fed the estimate — never their text or the tone
    // values the heuristic produced.
    properties: ['sample_count'],
    owner: 'Product Owner',
  },
  'human_layer.vision.statement_captured': {
    code: 'human_layer.vision.statement_captured',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause: 'Saving one canon category is not a use of anything to bill.',
    unit: 'save',
    surface: 'Settings · Human Layer · My Vision',
    emission: 'client — per-category save',
    // `step` reuses the same shaped surface key My Voice's per-section save
    // uses (`human_layer.voice.section_captured`, above) — which of the six
    // closed category ids was saved, never the statement text itself.
    properties: ['step'],
    owner: 'Product Owner',
  },

  /* -------------------------------------------------------- Tier B - Gym -- */

  'gym.capability.opened': {
    code: 'gym.capability.opened',
    tier: 'B',
    basis: B,
    billable: false,
    notBillableBecause:
      'Opening a linked module is navigation, not a use of anything to bill — the destination system meters its own commits.',
    unit: 'click',
    surface: 'Gym · capability map',
    emission: 'client — capability card, quick-create and about-us controls',
    // `surface` here is the card or action's permanent key (e.g. "portfolio",
    // "create-partner", "about-us") — never the resolved URL, which is an
    // internal tool address rather than something worth carrying on an event.
    properties: ['surface'],
    owner: 'Product Owner',
  },
} as const satisfies Record<string, EventDef>;

export type EventCode = keyof typeof EVENTS;
export type TierBCode = {
  [K in EventCode]: (typeof EVENTS)[K]['tier'] extends 'B' ? K : never;
}[EventCode];

export const eventDef = (code: EventCode): EventDef => EVENTS[code];

/**
 * Commit surfaces this product exposes, and the meter each one is covered by.
 *
 * A new user-facing commit either names its meter here or is explicitly marked as not
 * metered with a reason. The gate reads this, so silence fails the build rather than
 * shipping an unmetered billable action.
 */
export const COMMIT_SURFACES: readonly {
  readonly surface: string;
  readonly meter: EventCode | null;
  readonly notMeteredBecause?: string;
}[] = [
  { surface: 'MMM Integration · Accept & Kickoff', meter: 'mmm.engagement.committed' },
  { surface: 'MMM Integration · Confirm Results — accept', meter: 'mmm.dataset.accepted' },
  { surface: 'MMM Integration · MMM exchange — delivery passes', meter: 'mmm.delivery.validated' },
  { surface: 'Partnership Business Case · Calculate', meter: 'proi.case.calculated' },
  { surface: 'Partnership Business Case · Freeze', meter: 'proi.case.frozen' },
  {
    surface: 'MMM Integration · Offer deck export',
    meter: null,
    notMeteredBecause: 'Pre-sale collateral: the engagement it proposes is metered when committed.',
  },
  {
    surface: 'Data inventory · exports',
    meter: null,
    notMeteredBecause: 'Reporting on work already metered at the engagement commit.',
  },
  {
    surface: 'Lockerroom · catalogue — register for beta access',
    meter: null,
    notMeteredBecause:
      'Asking for something unbuilt. There is nothing to bill, and a fee here would suppress the only signal the register exists to produce.',
  },
];
